import * as React from 'react';
import * as _ from 'lodash';
import { useDispatch } from 'react-redux';
import {
  API,
  Notification,
  getUuidV4,
  Logging,
  NotificationData,
  ResponseError,
} from '@vcd-ecm-ui/Common';
import {
  GenericGrid,
  FilterModelApi,
  GridDataSummaryCollection,
  SortModelApi,
  mergeDefaultAndCustomColumns,
  ExpanderIcons,
  ColumnLink,
  ColumnButton,
} from '@vcd-ecm-ui/GenericGrid';
import { SideBySideViewer } from '@vcd-ecm-ui/SideBySideViewer';
import { VtxButton, VtxColumnProps } from '@vertexinc/vtx-ui-react-component-library';
import ErrorLogProps from './ErrorLogProps.interface';
import GridRowData, { GridDataNativeType } from './GridRowData';
import getDefaultColumns from './defaultColumns';
import '../common-reports.scss';
import './error-log.scss';

const ErrorLog = ({
  gatewayEndPoint,
}: ErrorLogProps): JSX.Element => {
  const [openSideBySideViewer, setOpenSideBySideViewer] = React.useState(false);
  const [selectedCertificate, setSelectedCertificate] = React.useState(null);
  const [manualRefreshKey, setManualRefreshKey] = React.useState(null);
  const [notification, setNotification] = React.useState(null);

  const dispatch = useDispatch();

  // Show the Side By Side Viewer
  const onShowCertificate = (certificateUuid: string): void => {
    setSelectedCertificate(certificateUuid);
    setOpenSideBySideViewer(true);
  };

  // Make the API Call to Delete the Error
  const deleteError = (id: number) => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);

      // Delete the Error
      await api.report.deleteErrorLogV41(id);

      // Set Manual Refresh Key
      setManualRefreshKey(getUuidV4());
    } catch (error) {
      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Error Message Delete Error',
        description: 'Unable to Delete the Error Message.',
        error: error as ResponseError,
      };

      setNotification(notificationData);
    }
  };

  // Delete Error
  const onDeleteError = (id: number): void => {
    dispatch(deleteError(id));
  };

  // Close the Side By Side Viewer
  const onSideBySideViewerClose = () => {
    // Close the Side By Size Viewer
    setOpenSideBySideViewer(false);
  };

  // Check if a Row can be Expanded
  const rowExpandable = (row: GridRowData): boolean => true;

  // Row is Expanded
  const expandedRowRender = (row: GridRowData): JSX.Element => {
    Logging.Debug('expandedRowRender', row);

    return (
      <>
        <p className="reportDetailRow">
          {row.rowData.message}
        </p>
      </>
    );
  };

  // Parse each row from the Native Data Type to the Actual Data Type
  const rowParser = (row: GridDataNativeType): GridRowData => GridRowData.fromData(row);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderAction = (value: any, row: GridRowData, index: number): JSX.Element => {
    switch (row.rowData.referenceType) {
      case 'CERTIFICATE':
        return (
          <ColumnLink
            id="downloadLink"
            linkText="Go to Certificate"
            onClick={(event) => onShowCertificate(row.rowData.referenceUuid)}
            align="center"
            row={row}
          />
        );
      case 'CUSTOMER':
        return (
          <ColumnLink
            id="downloadLink"
            linkText="Go to Customer"
            linkAddress={`#/customer/view/${row.rowData.referenceUuid}/1`}
            align="center"
            row={row}
          />
        );
      case 'UNKNOWN':
        if (!row.rowData.referenceUuid) {
          return (
            <ColumnButton
              id="deleteButton"
              buttonText="Delete Message"
              onClick={(event) => onDeleteError(row.rowData.id)}
              align="center"
              row={row}
            />
          );
        }
        break;
      default:
        return (<></>);
    }

    return (<></>);
  };

  // Get the Columns for the Grid
  const getColumns = (): VtxColumnProps<GridRowData>[] => {
    // Get the Default Columns
    const defaultColumns: VtxColumnProps<GridRowData>[] = getDefaultColumns();

    // Column Columns with Dynamic Renderer
    const customColumns = [
      {
        dataIndex: 'action',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        render: (value: any, row: GridRowData, index: number) => renderAction(value, row, index),
      },
    ];

    // Merge the Custom Columns into the Default Columns
    return mergeDefaultAndCustomColumns(defaultColumns, customColumns);
  };

  // The API Call used to retrieve data for the Grid
  const apiGetRowsServerModel = (
    startRow: number,
    endRow: number,
    filterModel: FilterModelApi,
    sortModel: SortModelApi,
  ): Promise<GridDataSummaryCollection> => {
    const api = new API(gatewayEndPoint);

    return api.report.getErrorLogsV41(startRow, endRow, filterModel, sortModel);
  };

  // Notification Error was Closed
  const onCloseNotification = (): void => {
    setNotification(null);
  };

  React.useEffect(() => {
    // This Code will run once after the initial Render
    Logging.Debug('Error Log: Mount');

    return (): void => {
      // This Code will run once the component unmounts.
    };
  }, []);

  const expanderIcons: ExpanderIcons = {
    collapseIcon: 'down',
    expandIcon: 'right',
  };

  return (
    <>
      <div className="reportsContainer">
        <div className="reportsHeader">
          <div className="reportsHeaderLeft">Error Log</div>
          <div className="reportsHeaderRight" />
        </div>
        <div className="reportsContentGrid">
          <GenericGrid<GridRowData>
            id="errorLogGrid"
            dataTypeDescription="Error Log"
            getDefaultColumns={getColumns}
            rowParser={rowParser}
            expandedRowRender={expandedRowRender}
            rowExpandable={rowExpandable}
            apiGetRowsServerModel={apiGetRowsServerModel}
            verticalHeightOffset={338}
            model="server"
            autoRefreshSeconds={60}
            expanderIcons={expanderIcons}
            useCache={false} // Cannot use cache if Auto Refresh
            horizontalWidth="max-width" // Do not scale to the Width of the Content
            refreshKey={manualRefreshKey}
          />
        </div>
      </div>
      <SideBySideViewer
        isOpen={openSideBySideViewer}
        onClose={onSideBySideViewerClose}
        certificateId={null}
        certificateUuid={selectedCertificate}
        gatewayEndPoint={gatewayEndPoint}
      />
      <Notification
        data={notification}
        onNotificationClose={onCloseNotification}
        autoClose
      />
    </>
  );
};

export default ErrorLog;
